import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { TopCompany } from "../../utils/AnalytivalDataType";
import { Box, useMediaQuery } from "@mui/material";

// Register the required components for chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface TopCompaniesChartProps {
  data: TopCompany[];
}

const TopCompaniesChart: React.FC<TopCompaniesChartProps> = ({ data }) => {
  const isMobile = useMediaQuery("(max-width:1200px)");

  // Create gradient for bars
  const createGradient = (ctx: CanvasRenderingContext2D, index: number) => {
    const gradient = ctx.createLinearGradient(0, 0, 600, 0);
    const colors = [
      { start: "rgba(99, 102, 241, 0.9)", end: "rgba(139, 92, 246, 0.7)" },
      { start: "rgba(236, 72, 153, 0.9)", end: "rgba(251, 113, 133, 0.7)" },
      { start: "rgba(16, 185, 129, 0.9)", end: "rgba(52, 211, 153, 0.7)" },
      { start: "rgba(245, 158, 11, 0.9)", end: "rgba(251, 191, 36, 0.7)" },
      { start: "rgba(59, 130, 246, 0.9)", end: "rgba(96, 165, 250, 0.7)" },
    ];
    const color = colors[index % colors.length];
    gradient.addColorStop(0, color.start);
    gradient.addColorStop(1, color.end);
    return gradient;
  };

  // Prepare chart data
  const chartData = {
    labels: data?.map(({ canonical_name }) => canonical_name), // Companies' names
    datasets: [
      {
        label: "Top Companies Hiring in Netherlands",
        data: data?.map(({ count }) => count), // Count for each company
        backgroundColor: function (context: any) {
          const ctx = context.chart.ctx;
          const index = context.dataIndex;
          return createGradient(ctx, index);
        },
        borderColor: "rgba(99, 102, 241, 0.3)",
        borderWidth: 0,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const, // Make the chart horizontal
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          font: {
            size: 13,
            weight: "bold" as const,
            family: "'Inter', sans-serif",
          },
          color: "#273848",
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        titleColor: "#273848",
        bodyColor: "#273848",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        titleFont: {
          size: 14,
          weight: "bold" as const,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: (tooltipItem: any) => {
            return ` Jobs: ${tooltipItem.raw.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: "rgba(148, 163, 184, 0.15)",
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          color: "#64748b",
          padding: 8,
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', sans-serif",
          },
          color: "#64748b",
        },
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: isMobile ? 320 : 420,
        padding: isMobile ? 2 : 3,
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      }}
    >
      <Bar data={chartData} options={options} />
    </Box>
  );
};

export default TopCompaniesChart;
